import { TrendingUp, Briefcase } from "lucide-react";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import { useCountUp } from "../../hooks/useCountUp";

// Animated counter component
const AnimatedCounter = ({ value, isVisible }) => {
  const animatedValue = useCountUp(value, 1800, isVisible);
  return <span>{animatedValue}</span>;
};

export const CareerSection = ({ careerSection }) => {
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.2 });

  if (!careerSection) return null;

  const { title, subtitle, description, vacancies, growth, salaryLevels, source } = careerSection;

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-[#F1EDE4]"
      data-testid="career-section"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className={`grid lg:grid-cols-2 gap-12 items-center transition-all duration-700 ease-out ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}>
          {/* Left */}
          <div>
            <span className="inline-flex items-center gap-2 bg-teal-500/10 text-teal-400 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-teal-500/20">
              <TrendingUp size={16} />
              Зарплаты в индустрии
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {title || "Сколько зарабатывают специалисты?"}
            </h2>
            <p className="text-gray-400 text-lg mb-6">
              {description || "Востребованная профессия с высоким потенциалом дохода"}
            </p>

            {/* Key stats with counter animation */}
            <div className="grid grid-cols-2 gap-4">
              <div className={`bg-white/5 rounded-xl p-4 transition-all duration-500 ease-out hover:bg-white/10 ${
                isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`} style={{ transitionDelay: "200ms" }}>
                <div className="flex items-center gap-2 mb-1">
                  <Briefcase size={18} className="text-teal-400" />
                  <span className="text-2xl font-bold text-teal-400">
                    <AnimatedCounter value={vacancies || "1 000+"} isVisible={isVisible} />
                  </span>
                </div>
                <div className="text-gray-500 text-sm">Вакансий на hh.ru</div>
              </div>
              <div className={`bg-white/5 rounded-xl p-4 transition-all duration-500 ease-out hover:bg-white/10 ${
                isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`} style={{ transitionDelay: "350ms" }}>
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp size={18} className="text-teal-400" />
                  <span className="text-2xl font-bold text-teal-400">
                    <AnimatedCounter value={growth || "+25%"} isVisible={isVisible} />
                  </span>
                </div>
                <div className="text-gray-500 text-sm">Рост спроса за год</div>
              </div>
            </div>
          </div>

          {/* Right - Salary bars with animation */}
          <div className="space-y-6">
            {salaryLevels?.map((level, idx) => (
              <div key={idx} className="group">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400 group-hover:text-gray-300 transition-colors">{level.title}</span>
                  <span className="text-white font-bold group-hover:text-teal-400 transition-colors">
                    <AnimatedCounter value={level.salary} isVisible={isVisible} />
                  </span>
                </div>
                <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ease-out ${
                      idx === 0 ? 'bg-gradient-to-r from-teal-600 to-teal-500' :
                      idx === 1 ? 'bg-gradient-to-r from-teal-500 to-emerald-500' :
                      'bg-gradient-to-r from-emerald-500 to-green-400'
                    }`}
                    style={{
                      width: isVisible ? (level.progress || `${33 + idx * 33}%`) : "0%",
                      transitionDelay: `${idx * 200 + 400}ms`
                    }}
                  />
                </div>
              </div>
            )) || (
              <>
                <div className="group">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400 group-hover:text-gray-300 transition-colors">Начинающий (0-1 год)</span>
                    <span className="text-white font-bold group-hover:text-teal-400 transition-colors">
                      <AnimatedCounter value="60 000 ₽" isVisible={isVisible} />
                    </span>
                  </div>
                  <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-teal-600 to-teal-500 rounded-full transition-all duration-1000 ease-out"
                      style={{
                        width: isVisible ? "35%" : "0%",
                        transitionDelay: "400ms"
                      }}
                    />
                  </div>
                </div>
                <div className="group">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400 group-hover:text-gray-300 transition-colors">Средний (1-3 года)</span>
                    <span className="text-white font-bold group-hover:text-teal-400 transition-colors">
                      <AnimatedCounter value="100 000 ₽" isVisible={isVisible} />
                    </span>
                  </div>
                  <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full transition-all duration-1000 ease-out"
                      style={{
                        width: isVisible ? "60%" : "0%",
                        transitionDelay: "600ms"
                      }}
                    />
                  </div>
                </div>
                <div className="group">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400 group-hover:text-gray-300 transition-colors">Профессионал (3+ лет)</span>
                    <span className="text-white font-bold group-hover:text-teal-400 transition-colors">
                      <AnimatedCounter value="150 000+ ₽" isVisible={isVisible} />
                    </span>
                  </div>
                  <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-green-400 rounded-full transition-all duration-1000 ease-out"
                      style={{
                        width: isVisible ? "100%" : "0%",
                        transitionDelay: "800ms"
                      }}
                    />
                  </div>
                </div>
              </>
            )}

            <p className="text-gray-600 text-sm pt-4">
              {source || "* По данным hh.ru и Хабр Карьера, 2025"}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
